import * as yup from 'yup';

export type FileState = {
  state: 'added' | 'error' | 'loading' | 'exists';
  errorMessage?: string;
  url?: string;
  fileType?: string;
};

export const storyFileStateSchema = yup.object({
  state: yup.string().oneOf(['added', 'error', 'loading', 'exists']).optional(),
  errorMessage: yup.string().optional(),
  url: yup.string().optional(),
  fileType: yup.string().optional(),
});

export enum StoryFields {
  ImageUrl = 'imageUrl',
  TitleImageUrl = 'titleImageUrl',
  SoundtrackUrl = 'soundtrackUrl',
  Title = 'title',
  StoryAbout = 'storyAbout',
  StoryEnd = 'storyEnd',
  AvilableDateRange = 'avilableDateRange',
  EnteryFee = 'enteryFee',
  StoryReward = 'storyReward',
  Chapters = 'chapters',
  Publisher = 'publisher',
}

export enum StoryChapterFields {
  RenderKey = 'renderKey',
  Difficulty = 'difficulty',
  ChapterIntroduction = 'chapterIntroduction',
  ChapterNumber = 'chapterNumber',
  Type = 'type',
  Id = 'firebaseId',
  Url = 'url',
  IconUrl = 'iconUrl',
  VideoIconUrl = 'iconAnimatedUrl',
  PreviewPlaceholderUrl = 'previewPlaceholderUrl',
  VideoPreviewUrl = 'previewVideoUrl',
  Original = 'original',
  Video = 'videoUrl',
  LowQualityImageUrl = 'lowQualityImageUrl',
  HighQualityImageUrl = 'highQualityImageUrl',
}

export const storyChapterSchema = yup.object({
  [StoryChapterFields.RenderKey]: yup.string().required(),
  [StoryChapterFields.Id]: yup.string().required(),
  [StoryChapterFields.Difficulty]: yup
    .object({
      label: yup.string(),
      value: yup.string().required(),
    })
    .required('Difficulty is missing'),
  [StoryChapterFields.ChapterIntroduction]: yup
    .string()
    .required('Chapter Introduction is missing'),
  [StoryChapterFields.ChapterNumber]: yup.number().required(),
  [StoryChapterFields.Type]: yup.string().oneOf(['static', 'video']).required(),
  [StoryChapterFields.Url]: storyFileStateSchema.required().optional(),
  [StoryChapterFields.IconUrl]: storyFileStateSchema.optional(),
  [StoryChapterFields.VideoIconUrl]: storyFileStateSchema.optional(),
  [StoryChapterFields.PreviewPlaceholderUrl]: storyFileStateSchema.optional(),
  [StoryChapterFields.VideoPreviewUrl]: storyFileStateSchema.optional(),
  [StoryChapterFields.Original]: storyFileStateSchema.optional(),
  [StoryChapterFields.Video]: storyFileStateSchema.optional(),
});
export type StoryChapterFieldsType = yup.InferType<typeof storyChapterSchema>;
export type StoryChapterMediaFieldsType =
  | StoryChapterFields.Url
  | StoryChapterFields.VideoIconUrl
  | StoryChapterFields.PreviewPlaceholderUrl
  | StoryChapterFields.IconUrl
  | StoryChapterFields.VideoPreviewUrl
  | StoryChapterFields.Video
  | StoryChapterFields.Original;

export const storyShema = yup.object({
  [StoryFields.TitleImageUrl]: storyFileStateSchema.optional(),
  [StoryFields.ImageUrl]: storyFileStateSchema.optional(),
  [StoryFields.SoundtrackUrl]: storyFileStateSchema.optional(),
  [StoryFields.Title]: yup.string().trim().required('Name is missing'),
  [StoryFields.StoryAbout]: yup.string().required('Story about is missing'),
  [StoryFields.StoryEnd]: yup.string().required('Story end is missing'),
  [StoryFields.AvilableDateRange]: yup
    .object({
      start: yup.date().optional(),
      end: yup.date().optional(),
    })
    .test('isRangeValie', 'AvilableDateRange', async (range, context) => {
      console.log(range);
      const { start, end } = range;
      const currentDate = new Date();
      currentDate.setHours(0);
      currentDate.setMinutes(0);
      currentDate.setSeconds(0);
      currentDate.setMilliseconds(0);

      const isInThePastArray = [
        { value: start, type: 'Start' },
        { value: end, type: 'End' },
      ]
        .map((dateValue) =>
          !dateValue?.value || currentDate < dateValue?.value
            ? undefined
            : `${dateValue?.value} can not be in the past`,
        )
        .find((item) => item !== undefined);
      if (isInThePastArray) {
        return context.createError({
          message: isInThePastArray,
        });
      }

      if (start && end) {
        if (end.getTime() === start.getTime()) {
          return context.createError({
            message: 'Start and end date can not be the same!',
          });
        }

        if (end.getTime() < start.getTime()) {
          return context.createError({
            message: 'Range end date must atleast a day in the futare compared to start date!',
          });
        }
      }

      return true;
    })
    .optional(),
  [StoryFields.EnteryFee]: yup
    .number()
    .min(0, 'Entery fee must be atleast 0')
    .required('Entery fee is missing'),
  [StoryFields.StoryReward]: yup
    .number()
    .moreThan(0, 'Reward must be higher than 0')
    .required('Story reward is missing'),
  [StoryFields.Chapters]: yup.array().of(storyChapterSchema).required('Chapters are missing'),
  [StoryFields.Publisher]: yup
    .object({
      label: yup.object<JSX.Element>(),
      value: yup.string().required(),
    })
    .required('Publisher must be selected'),
});

export type StoryFieldsType = yup.InferType<typeof storyShema>;
